/**
 * Selección del cuadro de turnos (008 · T045 · US1; contracts/ui.md §1.2).
 *
 * - **Clic**: agrega o quita la celda y la deja como ancla.
 * - **Shift+clic**: agrega el rectángulo ancla → celda (varios trabajadores × varios días).
 * - **Arrastre**: agrega el rectángulo origen → celda bajo el puntero mientras se arrastra.
 * - **Teclado**: flechas (e Inicio/Fin) mueven el foco; Espacio agrega o quita la celda enfocada;
 *   Shift+Espacio agrega el rectángulo desde el ancla; Escape limpia la selección.
 *
 * Solo entran celdas seleccionables (`isSelectable`: sin candado y con permiso de escritura). El
 * foco es "roving": una sola celda del cuadro tiene `tabIndex = 0`.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

/** Clave estable de una celda: `worker_id|YYYY-MM-DD`. */
export function cellKey(workerId: string, day: string): string {
  return `${workerId}|${day}`;
}

/** Separa una clave de celda en trabajador y día. */
export function parseCellKey(key: string): { workerId: string; day: string } {
  const index = key.lastIndexOf('|');
  return { workerId: key.slice(0, index), day: key.slice(index + 1) };
}

export interface GridPosition {
  row: number;
  col: number;
}

export interface UseGridSelectionOptions {
  /** Ids de trabajador, en el orden de las filas. */
  rows: readonly string[];
  /** Días (`YYYY-MM-DD`), en el orden de las columnas. */
  days: readonly string[];
  /** La celda puede entrar en la selección. */
  isSelectable: (key: string) => boolean;
}

export interface GridSelection {
  selected: ReadonlySet<string>;
  /** Celda con el foco "roving" (siempre dentro de la grilla si hay filas). */
  focus: GridPosition;
  setFocus: (position: GridPosition) => void;
  toggle: (position: GridPosition) => void;
  extendTo: (position: GridPosition) => void;
  /** Botón principal abajo sobre una celda: empieza un arrastre (o Shift+clic). */
  pointerDown: (position: GridPosition, shiftKey: boolean) => void;
  /** El puntero entra en otra celda durante un arrastre. */
  pointerEnter: (position: GridPosition) => void;
  clear: () => void;
  /** Reemplaza la selección (p. ej. tras guardar, para dejar solo las celdas con error). */
  replace: (keys: Iterable<string>) => void;
}

interface DragState {
  origin: GridPosition;
  base: ReadonlySet<string>;
  moved: boolean;
}

function clamp(value: number, max: number): number {
  return Math.max(0, Math.min(value, Math.max(max, 0)));
}

export function useGridSelection({
  rows,
  days,
  isSelectable,
}: UseGridSelectionOptions): GridSelection {
  const [selected, setSelectedState] = useState<ReadonlySet<string>>(() => new Set());
  // Copia sincrónica: los gestos (mousedown → mouseup) leen la selección sin esperar un render.
  const selectedRef = useRef<ReadonlySet<string>>(selected);
  const setSelected = useCallback((next: ReadonlySet<string>) => {
    selectedRef.current = next;
    setSelectedState(next);
  }, []);
  const [rawFocus, setRawFocus] = useState<GridPosition>({ row: 0, col: 0 });
  const anchor = useRef<GridPosition | null>(null);
  const drag = useRef<DragState | null>(null);

  // Referencias a lo último recibido: los manejadores globales (mouseup) no se recrean.
  const latest = useRef({ rows, days, isSelectable });
  useEffect(() => {
    latest.current = { rows, days, isSelectable };
  });

  const focus: GridPosition = {
    row: clamp(rawFocus.row, rows.length - 1),
    col: clamp(rawFocus.col, days.length - 1),
  };

  const keyAt = useCallback((position: GridPosition): string | null => {
    const workerId = latest.current.rows[position.row];
    const day = latest.current.days[position.col];
    return workerId !== undefined && day !== undefined ? cellKey(workerId, day) : null;
  }, []);

  const rectangle = useCallback(
    (a: GridPosition, b: GridPosition): string[] => {
      const keys: string[] = [];
      const [top, bottom] = a.row <= b.row ? [a.row, b.row] : [b.row, a.row];
      const [left, right] = a.col <= b.col ? [a.col, b.col] : [b.col, a.col];
      for (let row = top; row <= bottom; row += 1) {
        for (let col = left; col <= right; col += 1) {
          const key = keyAt({ row, col });
          if (key && latest.current.isSelectable(key)) {
            keys.push(key);
          }
        }
      }
      return keys;
    },
    [keyAt],
  );

  const toggle = useCallback(
    (position: GridPosition) => {
      anchor.current = position;
      const key = keyAt(position);
      if (!key || !latest.current.isSelectable(key)) {
        return;
      }
      const next = new Set(selectedRef.current);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      setSelected(next);
    },
    [keyAt, setSelected],
  );

  const extendTo = useCallback(
    (position: GridPosition) => {
      const origin = anchor.current ?? position;
      const keys = rectangle(origin, position);
      setSelected(new Set([...selectedRef.current, ...keys]));
      if (anchor.current === null) {
        anchor.current = position;
      }
    },
    [rectangle, setSelected],
  );

  const pointerDown = useCallback(
    (position: GridPosition, shiftKey: boolean) => {
      setRawFocus(position);
      if (shiftKey) {
        extendTo(position);
        return;
      }
      drag.current = { origin: position, base: selectedRef.current, moved: false };
    },
    [extendTo],
  );

  const pointerEnter = useCallback(
    (position: GridPosition) => {
      const state = drag.current;
      if (!state) {
        return;
      }
      if (state.origin.row === position.row && state.origin.col === position.col && !state.moved) {
        return;
      }
      state.moved = true;
      const keys = rectangle(state.origin, position);
      setSelected(new Set([...state.base, ...keys]));
    },
    [rectangle, setSelected],
  );

  // Fin del gesto en cualquier parte de la ventana: sin movimiento es un clic (alternar).
  useEffect(() => {
    function onPointerUp() {
      const state = drag.current;
      drag.current = null;
      if (!state) {
        return;
      }
      if (state.moved) {
        anchor.current = state.origin;
        return;
      }
      toggle(state.origin);
    }
    window.addEventListener('mouseup', onPointerUp);
    return () => window.removeEventListener('mouseup', onPointerUp);
  }, [toggle]);

  const clear = useCallback(() => {
    anchor.current = null;
    setSelected(new Set());
  }, [setSelected]);

  const replace = useCallback(
    (keys: Iterable<string>) => {
      setSelected(new Set(keys));
    },
    [setSelected],
  );

  return {
    selected,
    focus,
    setFocus: setRawFocus,
    toggle,
    extendTo,
    pointerDown,
    pointerEnter,
    clear,
    replace,
  };
}
